import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { shortenUrl, getOriginalUrl, testDatabaseConnection } from './firebase';
import './App.css';

type IconName = 'link' | 'arrow' | 'sparkle' | 'edit' | 'copy' | 'check' | 'qr' | 'download' | 'plus' | 'alert' | 'refresh';
function Icon({ name, className = '' }: { name: IconName; className?: string }) {
  const paths: Record<IconName, React.ReactNode> = {
    link: <><path d="M10 13a5 5 0 0 0 7 .2l3-3a5 5 0 0 0-7-7l-1.7 1.7" /><path d="M14 11a5 5 0 0 0-7-.2l-3 3a5 5 0 0 0 7 7l1.7-1.7" /></>,
    arrow: <path d="M5 12h14m-5-5 5 5-5 5" />,
    sparkle: <><path d="m12 3 2.4 6.6L21 12l-6.6 2.4L12 21l-2.4-6.6L3 12l6.6-2.4L12 3Z" /><path d="m20 2 .5 1.5L22 4l-1.5.5L20 6l-.5-1.5L18 4l1.5-.5L20 2Z" /></>,
    edit: <><path d="m15 5 4 4M5 19l4-1L20 7a2.8 2.8 0 0 0-4-4L5 14l-1 6 6-1" /><path d="M13 20h7" /></>,
    copy: <><rect x="8" y="8" width="12" height="12" rx="3" /><path d="M15 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h3" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    qr: <><rect x="3" y="3" width="6" height="6" rx="1" /><rect x="15" y="3" width="6" height="6" rx="1" /><rect x="3" y="15" width="6" height="6" rx="1" /><path d="M15 15h3v3h3v3h-6v-3m6-3v-3M12 3v3M3 12h3m6-3v6" /></>,
    download: <path d="M12 3v12m-4-4 4 4 4-4M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />,
    plus: <path d="M12 5v14M5 12h14" />,
    alert: <><circle cx="12" cy="12" r="9" /><path d="M12 7v6m0 4h.01" /></>,
    refresh: <><path d="M20 7v5h-5M4 17v-5h5" /><path d="M6 6a8 8 0 0 1 13 3M5 15a8 8 0 0 0 13 3" /></>,
  };
  return <svg className={`icon ${className}`} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

function App() {
  const [url, setUrl] = useState('');
  const [customText, setCustomText] = useState('');
  const [urlType, setUrlType] = useState<'simple' | 'custom'>('simple');
  const [shortUrl, setShortUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState('');
  const [invalidField, setInvalidField] = useState<'url' | 'custom' | null>(null);
  const [dbStatus, setDbStatus] = useState<boolean | null>(null);
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [redirectError, setRedirectError] = useState(false);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const customInputRef = useRef<HTMLInputElement>(null);
  const resultTitleRef = useRef<HTMLHeadingElement>(null);
  const qrRef = useRef<HTMLDivElement>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRedirectMode = (window as Window & { JAMMANBO_REDIRECT_MODE?: boolean }).JAMMANBO_REDIRECT_MODE === true;
  const isLinkPath = window.location.pathname !== '/';

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => { setToast(null); setIsCopied(false); }, 4000);
  }, []);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);
  useEffect(() => {
    const onOffline = () => setDbStatus(false);
    const onOnline = () => setConnectionAttempt(attempt => attempt + 1);
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, []);
  useEffect(() => {
    if (isRedirectMode || isLinkPath) return;
    let active = true;
    setDbStatus(null);
    testDatabaseConnection().then(connected => { if (active) setDbStatus(connected); }).catch(() => { if (active) setDbStatus(false); });
    return () => { active = false; };
  }, [isRedirectMode, isLinkPath, connectionAttempt]);
  useEffect(() => {
    if (isRedirectMode || !isLinkPath) return;
    let active = true;
    getOriginalUrl(window.location.pathname.substring(1)).then(originalUrl => {
      if (!active) return;
      if (originalUrl) window.location.replace(originalUrl);
      else setRedirectError(true);
    }).catch(() => { if (active) setRedirectError(true); });
    return () => { active = false; };
  }, [isRedirectMode, isLinkPath]);
  useEffect(() => { if (shortUrl) resultTitleRef.current?.focus(); }, [shortUrl]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading) return;
    setError(''); setInvalidField(null);
    const trimmedUrl = url.trim();
    const trimmedCustom = customText.trim().normalize('NFC');
    let formattedUrl = trimmedUrl;
    try {
      if (!trimmedUrl) throw new Error('짧게 만들 원본 URL을 입력해주세요.');
      if (/^[a-z][a-z\d+.-]*:/i.test(trimmedUrl) && !/^https?:\/\//i.test(trimmedUrl) && !/^[^\s/:?#]+:\d+(?:[/?#]|$)/.test(trimmedUrl)) throw new Error('http:// 또는 https://로 시작하는 웹 주소를 입력해주세요.');
      if (!/^https?:\/\//i.test(formattedUrl)) formattedUrl = `https://${formattedUrl}`;
      const parsedUrl = new URL(formattedUrl);
      if (!parsedUrl.hostname.includes('.') && parsedUrl.hostname !== 'localhost') throw new Error('example.com처럼 올바른 웹 주소를 입력해주세요.');
    } catch (validationError) {
      setError(validationError instanceof TypeError ? '올바른 웹 주소를 입력해주세요.' : (validationError as Error).message);
      setInvalidField('url'); urlInputRef.current?.focus(); return;
    }
    if (urlType === 'custom' && (!trimmedCustom || /[/?#%\\\s]/.test(trimmedCustom))) {
      setError(trimmedCustom ? '주소에는 공백, /, ?, #, %, \\ 문자를 사용할 수 없어요.' : '사용할 나만의 주소를 입력해주세요.');
      setInvalidField('custom'); customInputRef.current?.focus(); return;
    }
    if (!navigator.onLine) { setError('인터넷 연결을 확인한 뒤 다시 시도해주세요.'); return; }
    try {
      setIsLoading(true);
      const result = await shortenUrl(trimmedUrl, urlType === 'custom' ? trimmedCustom : undefined);
      setShortUrl(result.shortUrl); setDbStatus(true);
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : '';
      setError(/permission|unavailable|network|offline|quota/i.test(message) ? '지금은 링크를 저장할 수 없어요. 잠시 후 다시 시도해주세요.' : message || '링크를 만들지 못했어요. 잠시 후 다시 시도해주세요.');
    } finally { setIsLoading(false); }
  };
  const copyToClipboard = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(shortUrl); setIsCopied(true);
      showToast('링크를 복사했어요. 원하는 곳에 붙여넣어보세요.');
    } catch { showToast('자동 복사를 사용할 수 없어요. 위의 링크를 길게 누르거나 선택해 복사해주세요.', 'error'); }
  };
  const downloadQr = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    try {
      const serialized = new XMLSerializer().serializeToString(svg);
      const fileUrl = URL.createObjectURL(new Blob([serialized], { type: 'image/svg+xml;charset=utf-8' }));
      const downloadLink = document.createElement('a');
      downloadLink.href = fileUrl; downloadLink.download = 'jammanbo-link-qr.svg';
      document.body.appendChild(downloadLink); downloadLink.click(); downloadLink.remove();
      setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
      showToast('QR 코드 파일을 준비했어요. 다운로드를 확인해주세요.');
    } catch { showToast('QR 코드를 저장하지 못했어요. 다시 시도해주세요.', 'error'); }
  };
  const resetForm = () => {
    setShortUrl(''); setUrl(''); setCustomText(''); setError(''); setInvalidField(null); setIsCopied(false); setToast(null);
    requestAnimationFrame(() => urlInputRef.current?.focus());
  };

  if (isRedirectMode) return null;
  if (isLinkPath) return <div className="App redirect-view"><main className="glass-panel redirect-panel" aria-live="polite"><span className="brand-mark"><Icon name={redirectError ? 'alert' : 'link'} /></span><h1>{redirectError ? '링크를 열 수 없어요' : '링크로 이동하고 있어요'}</h1><p>{redirectError ? '주소를 다시 확인하거나 잠시 후 다시 시도해주세요.' : '달콤한 연결까지, 잠시만 기다려주세요.'}</p>{redirectError ? <a className="primary-button" href="/">새 링크 만들기 <Icon name="arrow" /></a> : <span className="spinner" aria-label="불러오는 중" />}</main></div>;

  return (
    <div className="App">
      <a className="skip-link" href="#shortener">링크 만들기로 바로가기</a>
      <div className="app-shell">
        <header className="app-header glass-control">
          <a className="brand" href="/" aria-label="잠만보 URL 홈"><span className="brand-mark"><Icon name="link" /></span><span>잠만보 <span className="brand-url">URL</span></span></a>
          <div className={`connection-status ${dbStatus === false ? 'is-offline' : dbStatus ? 'is-online' : 'is-checking'}`} role="status"><span className="status-dot" /><span>{dbStatus === null ? '연결 확인 중' : dbStatus ? '서비스 연결됨' : '연결 확인 필요'}</span></div>
        </header>
        <main className="main-content">
          <section id="shortener" className={`workbench glass-panel ${shortUrl ? 'has-result' : ''}`} aria-labelledby={shortUrl ? 'result-title' : 'form-title'}>
            {!shortUrl ? <>
              <div className="workbench-topline"><h1 id="form-title">링크 만들기</h1><span className="workbench-domain">jamb.kr <span>/</span></span></div>
              <div className="mode-switch" role="group" aria-label="단축 방식">
                <span className={`mode-selection ${urlType === 'custom' ? 'is-custom' : ''}`} aria-hidden="true" />
                <button type="button" aria-pressed={urlType === 'simple'} onClick={() => { setUrlType('simple'); setError(''); setInvalidField(null); }} disabled={isLoading}><Icon name="link" />빠른 단축</button>
                <button type="button" aria-pressed={urlType === 'custom'} onClick={() => { setUrlType('custom'); setError(''); setInvalidField(null); }} disabled={isLoading}><Icon name="edit" />나만의 주소</button>
              </div>
              <form onSubmit={handleSubmit} noValidate aria-busy={isLoading}>
                <div className="field-group">
                  <label htmlFor="url-input">원본 URL</label>
                  <div className={`input-surface ${invalidField === 'url' ? 'has-error' : ''}`}><Icon name="link" /><input ref={urlInputRef} id="url-input" name="url" type="text" inputMode="url" autoComplete="url" autoCapitalize="none" spellCheck={false} placeholder="https://example.com" value={url} onChange={event => { setUrl(event.target.value); if (invalidField === 'url') { setError(''); setInvalidField(null); } }} disabled={isLoading} aria-invalid={invalidField === 'url'} aria-describedby={invalidField === 'url' ? 'form-error' : 'url-hint'} required /></div>
                  <p id="url-hint" className="field-hint">나누고 싶은 링크를 붙여넣어주세요.</p>
                </div>
                {urlType === 'custom' && <div className="field-group custom-field"><label htmlFor="custom-input">나만의 주소</label><div className={`input-surface custom-input-surface ${invalidField === 'custom' ? 'has-error' : ''}`}><span className="domain-prefix">jamb.kr/</span><input ref={customInputRef} id="custom-input" name="customCode" type="text" autoComplete="off" autoCapitalize="none" spellCheck={false} maxLength={60} placeholder="나만의-주소" value={customText} onChange={event => { setCustomText(event.target.value); if (invalidField === 'custom') { setError(''); setInvalidField(null); } }} disabled={isLoading} aria-invalid={invalidField === 'custom'} aria-describedby={invalidField === 'custom' ? 'form-error' : 'custom-hint'} required /></div><p id="custom-hint" className="field-hint">기억하기 쉬운 이름으로. 한글 주소도 사용할 수 있어요.</p></div>}
                {error && <p className="form-error" id="form-error" role="alert"><Icon name="alert" /><span>{error}</span></p>}
                <button className="primary-button" type="submit" disabled={isLoading}>{isLoading ? <><span className="spinner" />링크를 만들고 있어요</> : <><span>링크 단축하기</span><Icon name="arrow" /></>}</button>
                <div className="form-footnote"><span className="tiny-sparkle">✦</span>{urlType === 'simple' ? '짧은 링크와 QR 코드, 한 번에 준비해드려요.' : '나만의 이름으로, 더 기억하기 쉬운 링크를 만들어보세요.'}</div>
              </form>
            </> : <div className="result-content">
              <div className="result-heading"><span className="success-mark"><Icon name="check" /></span><span className="result-eyebrow">작은 링크, 새로운 연결</span></div>
              <h1 id="result-title" ref={resultTitleRef} tabIndex={-1}>나누기 좋은 링크가 됐어요.</h1><p className="result-description">복사해서 전하거나, QR 코드로 함께 나눠보세요.</p>
              <div className="result-link-surface"><Icon name="link" /><a href={shortUrl} target="_blank" rel="noopener noreferrer" aria-label={`단축 URL ${shortUrl} 새 탭에서 열기`}>{shortUrl.replace(/^https?:\/\//, '')}</a></div>
              <button type="button" className="primary-button copy-button" onClick={copyToClipboard}><Icon name={isCopied ? 'check' : 'copy'} />{isCopied ? '복사했어요' : '링크 복사'}</button>
              <div className="qr-section"><div ref={qrRef} className="qr-image"><QRCodeSVG value={shortUrl} size={120} level="M" marginSize={4} fgColor="#3d343a" bgColor="#ffffff" title="단축 링크 QR 코드" /></div><div className="qr-detail"><h3>화면 너머로, 더 간편하게</h3><p>카메라로 스캔하면<br />바로 연결돼요.</p><button className="text-button" type="button" onClick={downloadQr}><Icon name="download" />QR 코드 저장</button></div></div>
              <button type="button" className="secondary-button" onClick={resetForm}><Icon name="plus" />새 링크 만들기</button>
            </div>}
          </section>
          {dbStatus === false && <div className="connection-notice" role="status"><Icon name="alert" /><p>서비스 연결을 확인하지 못했어요. 잠시 후 다시 시도해주세요.</p><button type="button" aria-label="서비스 연결 다시 확인" onClick={() => setConnectionAttempt(attempt => attempt + 1)}><Icon name="refresh" /></button></div>}
          <section className="features" aria-label="잠만보 URL의 세 가지 기능">
            <div className="feature"><span className="feature-icon"><Icon name="link" /></span><div><h3>한 번에 짧게</h3><p>긴 주소도 가볍게 정리</p></div></div>
            <div className="feature"><span className="feature-icon"><Icon name="edit" /></span><div><h3>나답게 만드는 주소</h3><p>기억에 남는 나만의 이름</p></div></div>
            <div className="feature"><span className="feature-icon"><Icon name="qr" /></span><div><h3>QR로 손쉽게</h3><p>스캔 한 번으로 이어지는 순간</p></div></div>
          </section>
        </main>
        <footer className="app-footer"><span className="footer-brand"><Icon name="link" />잠만보 URL</span><span className="footer-divider" /><p>당신의 연결에 작은 달콤함을.</p><span className="footer-year">© {new Date().getFullYear()}</span></footer>
      </div>
      <div className="toast-region" aria-live="polite" aria-atomic="true">{toast && <div className={`toast glass-control ${toast.type}`}><Icon name={toast.type === 'error' ? 'alert' : 'check'} /><span>{toast.message}</span></div>}</div>
    </div>
  );
}
export default App;
